import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { shows, WordIntroExercise } from '../../../student/mockLessonContent';
import { BLUE, cardSx, INTER, NAVY, POPPINS, SLATE, SUCCESS_TEXT, SURFACE_SOFT } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import InfoExpander from '../../../student/components/exercises/InfoExpander';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';

// "Dialog w/ Character Listen" — a new word. As in legacy, the student must hear
// the word before continuing. The meaning, picture and example sentence are
// teacher options (off unless switched on in the create-exercise page).
export default function WordIntroExerciseCard({ exercise, onReady }: { exercise: WordIntroExercise; onReady: ExerciseReady }) {
    const desktop = useExerciseLayout() === 'desktop';
    const [heard, setHeard] = React.useState(false);
    const showExample = shows(exercise, 'example') && exercise.example != null;

    const handlePlayed = () => {
        setHeard(true);
        onReady('done');
    };

    return (
        <Box>
            <ExerciseLabel>NEW WORD</ExerciseLabel>
            {exercise.teacher?.intro && <Box sx={{ mb: 1.5 }}><TeacherNote text={exercise.teacher.intro} /></Box>}
            <Box sx={{ ...cardSx, p: desktop ? 3.5 : 3 }}>
                <Box sx={{ display: desktop && showExample ? 'grid' : 'block', gridTemplateColumns: 'minmax(210px, .8fr) minmax(0, 1.2fr)', gap: 3.5, alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        {shows(exercise, 'picture') && (
                            <Box sx={{
                                width: 96, height: 96, borderRadius: '24px', mx: 'auto', mb: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: `${BLUE}14`,
                            }}>
                                <Typography role="img" aria-label={exercise.meaning} sx={{ fontSize: 48, lineHeight: 1 }}>{exercise.emoji}</Typography>
                            </Box>
                        )}
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 40, color: NAVY, lineHeight: 1.2 }}>
                            {exercise.hanzi}
                        </Typography>
                        {shows(exercise, 'pinyin') && (
                            <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 16, color: BLUE, mt: 0.5 }}>
                                {exercise.pinyin}
                            </Typography>
                        )}
                        {shows(exercise, 'english') && (
                            <Typography sx={{ fontFamily: INTER, fontSize: 15, color: SLATE, mt: 0.5 }}>
                                {exercise.meaning}
                            </Typography>
                        )}
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <AudioPlayButton ariaLabel={`Play audio for ${exercise.hanzi}`} size={52} required onPlayed={handlePlayed} />
                        </Box>
                        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                            {heard && <CheckCircleRoundedIcon sx={{ fontSize: 16, color: SUCCESS_TEXT }} />}
                            <Typography sx={{ fontFamily: INTER, fontSize: 12, color: heard ? SUCCESS_TEXT : SLATE }}>
                                {heard ? 'Heard — play it again any time' : 'Listen to the word to continue'}
                            </Typography>
                        </Stack>
                        {shows(exercise, 'info') && exercise.infoText && <InfoExpander text={exercise.infoText} label="About this word" />}
                    </Box>

                    {showExample && exercise.example && (
                        <Box sx={{ mt: desktop ? 0 : 3 }}>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE, mb: 1, textAlign: 'left' }}>
                                {desktop ? 'EXAMPLE IN CONTEXT' : 'EXAMPLE'}
                            </Typography>
                            <Box sx={{
                                p: desktop ? 2.5 : 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT,
                                display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left',
                            }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: desktop ? 20 : 18, color: NAVY, lineHeight: 1.4 }}>
                                        {exercise.example.hanzi}
                                    </Typography>
                                    {shows(exercise, 'pinyin') && (
                                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                            {exercise.example.pinyin}
                                        </Typography>
                                    )}
                                    {shows(exercise, 'english') && exercise.example.english && (
                                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                            {exercise.example.english}
                                        </Typography>
                                    )}
                                </Box>
                                <AudioPlayButton ariaLabel="Play example audio" size={desktop ? 36 : 32} />
                            </Box>
                        </Box>
                    )}
                </Box>
                <ExerciseSupport exercise={exercise} />
            </Box>
        </Box>
    );
}
